import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { allItems, describeGoal, describeRange, FIX_BY_LABELS } from '@bp/shared';
import { api, mediaUrl } from '@/lib/api';
import type { AuditDetail } from '@/lib/types';
import { cn, fmtDate, fmtDateTime } from '@/lib/utils';
import { Button, ErrorNote, Loading } from '@/components/ui';
import { LaborResultsTable } from '@/components/audit/LaborPanel';

// Print-friendly summary: open, then Print / Save as PDF from the browser.
export function AuditPrintPage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['audit', id], queryFn: () => api.get<AuditDetail>(`/api/audits/${id}`) });
  useEffect(() => {
    if (q.data) document.title = `${q.data.store.name} — ${q.data.template.name} — ${q.data.audit.audit_date}`;
  }, [q.data]);
  if (q.isLoading) return <Loading />;
  if (q.error) return <div className="p-6"><ErrorNote error={q.error} /></div>;
  const d = q.data!;
  const a = d.audit;
  const content = d.template.content;
  const byKey = new Map(d.responses.map((r) => [r.item_key, r]));
  const failed = allItems(content).filter((i) => byKey.get(i.key)?.result === 'fail');
  const tasks = new Map(d.action_items.map((t) => [t.response_id, t]));
  const photos = (key: string) => d.media.filter((m) => m.item_key === key && m.kind === 'photo');
  const rules = content.scoring_rules;
  const tone = (v: number) => (v >= rules.passing_threshold ? 'text-pass' : v >= rules.warning_threshold ? 'text-warn' : 'text-fail');
  const sectionLabel = (k: string | null) => content.sections.find((s) => s.key === k)?.label;

  return (
    <div className="mx-auto max-w-4xl bg-white p-8 text-[13px] text-fg">
      <div className="no-print mb-6 flex justify-end gap-2">
        <Button variant="outline" onClick={() => window.close()}>Close</Button>
        <Button onClick={() => window.print()}>Print / Save PDF</Button>
      </div>

      <header className="flex items-start justify-between border-b-2 border-primary pb-4">
        <div>
          <p className="text-xs font-semibold tracking-widest text-muted-fg uppercase">{a.mode === 'practice' ? 'Practice audit' : 'Official audit'}{a.status !== 'completed' && ' — DRAFT'}</p>
          <h1 className="mt-1 text-2xl font-bold">{d.store.name} <span className="font-normal text-muted-fg">#{d.store.store_number}</span></h1>
          <p className="mt-1">{d.template.name} (v{d.template.version_no}) · {fmtDate(a.audit_date)} · Auditor: {a.auditor_name}</p>
          {a.locked_at && <p className="text-xs text-muted-fg">Locked {fmtDateTime(a.locked_at)}</p>}
        </div>
        <div className="text-right">
          <p className={cn('text-4xl font-bold', tone(d.summary.score))}>{d.summary.score}%</p>
          <p className="text-xs text-muted-fg">{d.summary.passed} pass · {d.summary.failed} fail · {d.summary.na} N/A</p>
        </div>
      </header>

      <section className="mt-5 grid grid-cols-2 gap-6">
        <div>
          <h2 className="mb-2 font-semibold">Sections</h2>
          <table className="w-full"><tbody>
            {content.sections.map((s) => <tr key={s.key} className="border-b"><td className="py-1">{s.label}</td><td className={cn('py-1 text-right font-semibold', tone(d.summary.section_scores[s.key]))}>{d.summary.section_scores[s.key]}%</td></tr>)}
          </tbody></table>
        </div>
        <div>
          {a.goal_results.length > 0 && (
            <>
              <h2 className="mb-2 font-semibold">Goals</h2>
              <table className="w-full"><tbody>
                {a.goal_results.map((g) => <tr key={g.goal_id} className="border-b"><td className="py-1">{describeGoal(g, sectionLabel(g.section_key))}</td><td className={cn('py-1 text-right font-semibold', g.met ? 'text-pass' : 'text-fail')}>{g.met ? 'Met' : 'Missed'} ({g.actual})</td></tr>)}
              </tbody></table>
            </>
          )}
          {content.labor_config.enabled && d.labor_results.correct_hours !== null && (
            <>
              <h2 className="mt-4 mb-1 font-semibold">Labor</h2>
              <LaborResultsTable r={d.labor_results} />
            </>
          )}
        </div>
      </section>

      <section className="mt-6">
        <h2 className="mb-2 font-semibold">Failed items ({failed.length})</h2>
        {failed.length === 0 ? <p className="text-muted-fg">None.</p> : failed.map((item) => {
          const r = byKey.get(item.key)!;
          const t = tasks.get(r.id);
          return (
            <div key={item.key} className="print-break border-b py-2">
              <p className="font-medium">{item.text}</p>
              <p className="text-xs text-muted-fg">
                {sectionLabel(r.section_key)}
                {r.temperature !== null && ` · reading ${r.temperature}° (required ${describeRange(item)})`}
                {r.fix_by_window && ` · fix by ${FIX_BY_LABELS[r.fix_by_window]}`}
                {t && ` · assigned to ${t.assignee_name || 'unassigned'}${t.due_date ? `, due ${fmtDate(t.due_date)}` : ''}`}
              </p>
              {r.comment && <p className="mt-1">“{r.comment}”</p>}
              {photos(item.key).length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-2">{photos(item.key).slice(0, 4).map((m) => <img key={m.id} src={mediaUrl(m.id)} alt="" className="h-24 rounded border object-cover" />)}</div>
              )}
            </div>
          );
        })}
      </section>

      {a.notes && <section className="print-break mt-6"><h2 className="mb-1 font-semibold">Notes</h2><p className="whitespace-pre-wrap">{a.notes}</p></section>}

      <section className="mt-6">
        <h2 className="mb-2 font-semibold">All items</h2>
        {content.sections.map((s) => (
          <div key={s.key} className="print-break mb-3">
            <p className="bg-muted px-2 py-1 text-xs font-semibold uppercase">{s.label}</p>
            <table className="w-full"><tbody>
              {s.items.map((i) => {
                const r = byKey.get(i.key);
                const res = r?.result ?? 'pending';
                return (
                  <tr key={i.key} className="border-b align-top">
                    <td className="py-1 pr-2">{i.text}{r?.comment && <span className="block text-xs text-muted-fg">“{r.comment}”</span>}</td>
                    <td className={cn('w-24 py-1 text-right font-medium', res === 'pass' && 'text-pass', res === 'fail' && 'text-fail')}>
                      {res === 'na' ? 'N/A' : res === 'pending' ? '—' : res === 'pass' ? 'Pass' : 'Fail'}{r?.temperature !== null && r?.temperature !== undefined && ` ${r.temperature}°`}
                    </td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
        ))}
      </section>
      <p className="mt-6 text-center text-xs text-muted-fg">Printed {fmtDateTime(new Date())}</p>
    </div>
  );
}
